/**
 * Runtime Configuration System
 *
 * This module provides a comprehensive runtime configuration system that:
 * - Loads configuration from public/config.json before app initialization
 * - Supports Node.js updates to the config file
 * - Provides runtime variable changes with hot reload
 * - Integrates with @t3-oss/env for validation
 * - Supports environment-specific configurations
 * - Includes feature flags and API endpoint management
 */

import { createEnv } from "@t3-oss/env-core";
import { z } from "zod";

// Import shared configuration types and schemas
import {
	type ApiConfig,
	ApiConfigSchema,
	type AppConfig,
	AppConfigSchema,
	type EnvironmentConfig,
	type EnvironmentName,
	type Features,
	FeaturesSchema,
	type Flags,
	type Integrations,
	type MetadataSchema,
	type RuntimeConfig,
	RuntimeConfigSchema,
	type SecurityConfig,
	type UiConfig,
} from "./types/config.types.ts";

// =============================================================================
// RE-EXPORT SHARED TYPES FOR BACKWARD COMPATIBILITY
// =============================================================================

// Re-export all types for backward compatibility with existing imports
export type {
	AppConfig,
	ApiConfig,
	Flags,
	Features,
	UiConfig,
	Integrations,
	SecurityConfig,
	EnvironmentConfig,
	RuntimeConfig,
	EnvironmentName,
};

/**
 * Resolved configuration (after environment merging)
 * This represents the final config that components will use
 */
export interface ResolvedConfig {
	app: AppConfig;
	api: ApiConfig;
	flags: Flags;
	features: Features; // Backward compatibility - same as flags
	ui: UiConfig;
	integrations: Integrations;
	security: SecurityConfig;
	_metadata: z.infer<typeof MetadataSchema>;
}

// =============================================================================
// ENVIRONMENT VALIDATION (T3 ENV)
// =============================================================================

/**
 * Environment variables validation using @t3-oss/env
 */
const env = createEnv({
	clientPrefix: "VITE_",
	client: {
		VITE_CONFIG_URL: z.string().url().optional(),
		VITE_ENVIRONMENT: z
			.enum(["local", "development", "qa", "production"])
			.optional(),
		VITE_API_BASE_URL: z.string().url().optional(),
		VITE_FEATURE_FLAGS_OVERRIDE: z.string().optional(),
	},
	runtimeEnv: import.meta.env,
	emptyStringAsUndefined: true,
});

// =============================================================================
// CONFIGURATION STATE
// =============================================================================

let runtimeConfig: RuntimeConfig | null = null;
let resolvedConfig: ResolvedConfig | null = null;
let configLoadPromise: Promise<ResolvedConfig> | null = null;

// Configuration change listeners
const configChangeListeners = new Set<(config: ResolvedConfig) => void>();

// =============================================================================
// CONFIGURATION LOADING
// =============================================================================

/**
 * Load configuration from public/config.json
 */
async function loadConfigFromFile(): Promise<RuntimeConfig> {
	const configUrl = env.VITE_CONFIG_URL || "/config.json";

	try {
		// console.log("🔧 Config: Loading configuration from", configUrl);

		// Add a small delay to ensure DOM is ready
		await new Promise((resolve) => setTimeout(resolve, 100));

		const response = await fetch(configUrl, {
			cache: "no-cache",
			headers: {
				"Cache-Control": "no-cache",
				Pragma: "no-cache",
			},
		});

		if (!response.ok) {
			throw new Error(
				`Failed to load config: ${response.status} ${response.statusText}`,
			);
		}

		const configData = await response.json();

		// Validate configuration against schema
		const validatedConfig = RuntimeConfigSchema.parse(configData);

		// console.log("✅ Config: Configuration loaded and validated successfully");
		return validatedConfig;
	} catch (error) {
		console.error("❌ Config: Failed to load configuration:", error);
		throw new Error(`Configuration load failed from ${configUrl}`, {
			cause: error,
		});
	}
}
/**
 * Resolve configuration by merging environment-specific overrides
 */
function resolveConfig(config: RuntimeConfig): ResolvedConfig {
	const environment = env.VITE_ENVIRONMENT || config.app.environment;
	const envConfig =
		config.environments[environment as keyof typeof config.environments];

	if (!envConfig) {
		throw new Error(
			`❌ Config: Environment '${environment}' not found in configuration`,
		);
	}

	// In the new structure, we take everything from the environment config
	// since there are no top-level sections anymore
	const resolvedApiConfig: ApiConfig = { ...envConfig.api };
	const resolvedFlags: Flags = { ...envConfig.flags };

	// Apply environment variable overrides
	if (env.VITE_API_BASE_URL) {
		resolvedApiConfig.baseUrl = env.VITE_API_BASE_URL;
	}

	// Apply feature flags override from environment
	if (env.VITE_FEATURE_FLAGS_OVERRIDE) {
		try {
			const featureOverrides = JSON.parse(env.VITE_FEATURE_FLAGS_OVERRIDE);
			Object.assign(resolvedFlags, featureOverrides);
		} catch (error) {
			console.warn("⚠️ Config: Invalid feature flags override format:", error);
		}
	}

	const resolved: ResolvedConfig = {
		app: {
			...config.app,
			environment: environment as AppConfig["environment"],
		},
		api: resolvedApiConfig,
		flags: resolvedFlags,
		features: resolvedFlags, // Backward compatibility - features is same as flags
		ui: envConfig.ui,
		integrations: envConfig.integrations,
		security: envConfig.security,
		_metadata: config._metadata,
	};

	// console.log(
	// 	`🎯 Config: Resolved configuration for environment: ${environment}`,
	// );
	return resolved;
}

/**
 * Initialize configuration system
 */
export async function initializeConfig(): Promise<ResolvedConfig> {
	// Return existing config if already loaded
	if (resolvedConfig) {
		// console.log(
		// 	"🔧 Config: Configuration already initialized, returning existing config",
		// );
		return resolvedConfig;
	}

	// Return existing promise if initialization is in progress
	if (configLoadPromise) {
		// console.log(
		// 	"🔧 Config: Configuration initialization in progress, waiting...",
		// );
		return configLoadPromise;
	}

	// console.log("🔧 Config: Starting configuration initialization...");

	configLoadPromise = (async () => {
		try {
			// Load raw configuration
			runtimeConfig = await loadConfigFromFile();

			// Resolve with environment overrides
			resolvedConfig = resolveConfig(runtimeConfig);

			// Attach to window for runtime access
			attachConfigToWindow(resolvedConfig);

			// Set up polling for configuration changes (disabled by default)
			setupConfigPolling();

			// console.log(
			// 	"✅ Config: Configuration initialization completed successfully",
			// );
			return resolvedConfig;
		} catch (error) {
			console.error("❌ Config: Configuration initialization failed:", error);
			// Reset promise so retry is possible
			configLoadPromise = null;
			throw error;
		}
	})();

	return configLoadPromise;
}

/**
 * Reload configuration (for runtime updates)
 */
export async function reloadConfig(): Promise<ResolvedConfig> {
	const previousRuntimeConfig = runtimeConfig;
	const previousResolvedConfig = resolvedConfig;

	try {
		console.log("🔄 Config: Reloading configuration...");

		// Reset state to force fresh load
		configLoadPromise = null;
		resolvedConfig = null;
		runtimeConfig = null;

		// Load fresh configuration
		const newConfig = await initializeConfig();

		// Notify listeners of configuration change
		for (const listener of configChangeListeners) {
			try {
				listener(newConfig);
			} catch (error) {
				console.error("❌ Config: Error in change listener:", error);
			}
		}

		// Emit custom event for components
		window.dispatchEvent(
			new CustomEvent("config-changed", {
				detail: { config: newConfig },
			}),
		);

		console.log("✅ Config: Configuration reloaded successfully");
		return newConfig;
	} catch (error) {
		runtimeConfig = previousRuntimeConfig;
		resolvedConfig = previousResolvedConfig;
		console.error("❌ Config: Configuration reload failed:", error);
		throw error;
	}
}

// =============================================================================
// CONFIGURATION ACCESS
// =============================================================================

/**
 * Get current configuration (throws if not initialized)
 */
export function getConfig(): ResolvedConfig {
	if (!resolvedConfig) {
		throw new Error(
			"Configuration not initialized. Call initializeConfig() first.",
		);
	}
	return resolvedConfig;
}

/**
 * Get configuration safely (returns null if not initialized)
 */
export function getConfigSafe(): ResolvedConfig | null {
	return resolvedConfig;
}

/**
 * Check if configuration is loaded
 */
export function isConfigLoaded(): boolean {
	return resolvedConfig !== null;
}

/**
 * Wait for configuration to be loaded
 */
export async function waitForConfig(): Promise<ResolvedConfig> {
	if (resolvedConfig) {
		return resolvedConfig;
	}

	if (configLoadPromise) {
		return configLoadPromise;
	}

	return initializeConfig();
}

// =============================================================================
// CONFIGURATION UTILITIES
// =============================================================================

/**
 * Get API endpoint URL
 */
export function getApiEndpoint(endpoint: keyof ApiConfig["endpoints"]): string {
	const config = getConfig();
	return `${config.api.baseUrl}${config.api.endpoints[endpoint]}`;
}

/**
 * Check if feature is enabled
 */
export function isFeatureEnabled(feature: keyof Features): boolean {
	const config = getConfigSafe();
	return config?.features[feature] ?? false;
}

/**
 * Get environment-specific value
 */
export function getEnvironment(): AppConfig["environment"] {
	const config = getConfigSafe();
	return config?.app.environment ?? "development";
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
	return getEnvironment() === "development";
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
	return getEnvironment() === "production";
}

// =============================================================================
// RUNTIME UPDATES
// =============================================================================

/**
 * Add configuration change listener
 */
export function onConfigChange(
	listener: (config: ResolvedConfig) => void,
): () => void {
	configChangeListeners.add(listener);

	// Return unsubscribe function
	return () => {
		configChangeListeners.delete(listener);
	};
}

/**
 * Set up polling for configuration changes (disabled by default)
 * Only enable this for specific development scenarios where you need hot config reloading
 */
function setupConfigPolling(): void {
	// Disable polling - user doesn't want it
	const enablePolling = false; // Disabled per user request

	if (!enablePolling) {
		// console.log(
		// 	"🔧 Config: Polling disabled (config loads once on initialization)",
		// );
		return;
	}

	// Only set up polling in browser environment
	if (typeof window === "undefined" || !resolvedConfig) {
		return;
	}

	let lastModified = resolvedConfig._metadata.lastUpdated;

	const pollInterval = setInterval(async () => {
		try {
			const response = await fetch("/config.json", {
				method: "HEAD",
				cache: "no-cache",
			});

			if (response.ok) {
				const currentModified = response.headers.get("last-modified");

				if (currentModified && currentModified !== lastModified) {
					console.log("🔄 Config: Detected configuration change, reloading...");
					lastModified = currentModified;
					await reloadConfig();
				}
			}
		} catch (error) {
			// Silently ignore polling errors in development
			console.debug("Config polling error (ignored):", error);
		}
	}, 10000); // Poll every 10 seconds (less aggressive)

	// Clean up on page unload
	if (typeof window !== "undefined") {
		window.addEventListener("beforeunload", () => {
			clearInterval(pollInterval);
		});
	}
}

// =============================================================================
// WINDOW OBJECT INTEGRATION
// =============================================================================

/**
 * Attach configuration to window object for runtime access
 */
function attachConfigToWindow(config: ResolvedConfig): void {
	// Only attach to window in browser environment
	if (typeof window === "undefined") {
		return;
	}

	try {
		// Attach configuration to window
		(window as unknown as Record<string, unknown>).__fawkesConfig = {
			// Configuration access
			get: () => getConfigSafe() || config,
			getApi: () => (getConfigSafe() || config).api,
			getFeatures: () => (getConfigSafe() || config).features,
			getApp: () => (getConfigSafe() || config).app,

			// Utilities
			getApiEndpoint,
			isFeatureEnabled,
			getEnvironment,
			isDevelopment,
			isProduction,

			// Runtime updates
			reload: reloadConfig,
			onChange: onConfigChange,

			// Manual reload function (use this instead of automatic polling)
			forceReload: async () => {
				console.log("🔄 Config: Manual reload requested...");
				return await reloadConfig();
			},

			// Debug info
			debug: () => ({
				raw: runtimeConfig,
				resolved: getConfigSafe() || config,
				environment: getEnvironment(),
				loadTime: (getConfigSafe() || config)._metadata.lastUpdated,
				pollingEnabled: false, // Now disabled by default
			}),
		};

		// console.log("🪟 Config: Configuration attached to window.__fawkesConfig");

		// Development utilities
		if (config.app.environment === "development") {
			console.log("🔧 Config: Development utilities available:");
			console.log("  - window.__fawkesConfig.get() - Get current config");
			console.log(
				"  - window.__fawkesConfig.forceReload() - Manually reload config",
			);
			console.log("  - window.__fawkesConfig.debug() - Debug info");
			console.log(
				"  - window.__fawkesConfig.isFeatureEnabled(feature) - Check feature",
			);
			console.log(
				"  - Automatic polling is disabled (use forceReload() when needed)",
			);
		}
	} catch (error) {
		console.warn("⚠️ Config: Failed to attach to window object:", error);
	}
}

// =============================================================================
// LEGACY COMPATIBILITY
// =============================================================================

/**
 * Legacy compatibility with existing runtime-config.ts
 * Maps new config system to old interface
 */
export function getLegacyRuntimeEnv(key: string, fallback?: unknown): unknown {
	const config = getConfigSafe();
	if (!config) return fallback;

	switch (key) {
		case "VITE_API_MODE":
			return "real";
		case "VITE_API_BASE_URL":
			return config.api.baseUrl;
		case "VITE_APP_TITLE":
			return config.app.title;
		default:
			return fallback;
	}
}

// =============================================================================
// EXPORTS
// =============================================================================

export {
	RuntimeConfigSchema,
	AppConfigSchema,
	ApiConfigSchema,
	FeaturesSchema,
	env,
};

/**
 * Reset configuration state (for testing only)
 * @internal
 */
export function __resetConfigForTesting(): void {
	runtimeConfig = null;
	resolvedConfig = null;
	configLoadPromise = null;
	configChangeListeners.clear();

	// Also clear window object
	if (
		typeof window !== "undefined" &&
		(window as unknown as Record<string, unknown>).__fawkesConfig
	) {
		(window as unknown as Record<string, unknown>).__fawkesConfig = undefined;
	}
}

// Default export for convenience
export default {
	initialize: initializeConfig,
	get: getConfig,
	getSafe: getConfigSafe,
	reload: reloadConfig,
	waitFor: waitForConfig,
	isLoaded: isConfigLoaded,
	onConfigChange,
	getApiEndpoint,
	isFeatureEnabled,
	getEnvironment,
	isDevelopment,
	isProduction,
};
